import { useState, useEffect } from 'react';
import { goalService, type Goal } from '../../../services/goalService';
import { useNavigate } from 'react-router-dom';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';

interface GoalSettingProps {
  onTooltipShow: (title: string, content: string) => void;
}

// Goal type to UI mapping
const goalTypeToUI: Record<string, any> = {
  'organic-traffic': {
    title: 'Increase Organic Traffic',
    description: 'Monthly organic visitor growth',
    icon: 'ri-user-line',
    color: 'from-green-500 to-emerald-600',
    tooltip: 'Target organic traffic increase over the next 3 months'
  },
  'keyword-rankings': {
    title: 'Improve Keyword Rankings',
    description: 'Target distribution across ranking slabs',
    icon: 'ri-trophy-line',
    color: 'from-green-500 to-emerald-600',
    tooltip: 'Percentage of keywords expected to rank in each slab'
  },
  'serp-features': {
    title: 'Captured Visibility',
    description: 'SERP feature presence tracking across 20 keywords',
    icon: 'ri-star-line',
    color: 'from-green-500 to-emerald-600',
    tooltip: 'Captured Visibility — Tracking Metric\n\nThis card monitors your brand presence across SERP features (Featured Snippets, Knowledge Panels, etc.) for all 20 locked keywords.\n\nSnippet presence is highly dependent on your brand type and keyword context, so no fixed numeric target can be set. Target: Not Applicable.'
  },
  'avg-position': {
    title: 'Reduce Average Position',
    description: 'Expected improvement in keyword position',
    icon: 'ri-arrow-down-line',
    color: 'from-orange-500 to-red-600',
    tooltip: 'Expected improvement in average keyword position'
  },
  'impressions': {
    title: 'Increase Impressions',
    description: 'Monthly impression growth',
    icon: 'ri-eye-line',
    color: 'from-purple-500 to-violet-600',
    tooltip: 'Expected impression growth benchmark'
  },
  'domain-authority': {
    title: 'Improve Domain Authority',
    description: 'DA growth benchmark',
    icon: 'ri-shield-check-line',
    color: 'from-orange-500 to-red-600',
    tooltip: 'Domain authority improvement target'
  }
};

export default function GoalSetting({ onTooltipShow }: GoalSettingProps) {
  const navigate = useNavigate();
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [canRefresh, setCanRefresh] = useState(false);
  const { trackAction } = useTelemetry({ module: MODULES.SEO_MANAGER, submodule: 'Goal Setting' });

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    if (!userId) {
      window.location.href = '/login';
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchGoals();
  }, [userId]);

  const fetchGoals = async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await goalService.fetchGoals(userId);

      // If no goals exist, try to initialize them automatically
      if (response.data.goals.length === 0 && response.data.can_refresh) {
        console.log('No goals found, attempting to initialize...');
        try {
          trackAction(ACTIONS.SET_GOALS, 'Initialize Goals (Auto)', { user_id: userId });
          await goalService.initializeGoals(userId);
          // Refetch after initialization
          const refreshedResponse = await goalService.fetchGoals(userId);
          setGoals(refreshedResponse.data.goals);
          setCanRefresh(refreshedResponse.data.can_refresh);
        } catch (initErr) {
          console.error('Error auto-initializing goals:', initErr);
          // Still set the empty goals - user might need to complete onboarding
          setGoals(response.data.goals);
          setCanRefresh(response.data.can_refresh);
        }
      } else {
        setGoals(response.data.goals);
        setCanRefresh(response.data.can_refresh);
      }
    } catch (err) {
      console.error('Error fetching goals:', err);
      setError(err instanceof Error ? err.message : 'Failed to load goals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshGoals = async () => {
    if (!userId || !canRefresh) return;

    try {
      setIsLoading(true);
      trackAction(ACTIONS.SET_GOALS, 'Refresh Goals (Manual)', { user_id: userId });
      await goalService.refreshGoals(userId);
      await fetchGoals(); // Reload goals after refresh
    } catch (err) {
      console.error('Error refreshing goals:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh goals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateActionPlan = () => {
    // Navigate to action plan section
    navigate('/dashboard#action-plan');
  };

  if (!userId) {
    return null;
  }

  // Separate goals into priority and additional
  const priorityGoals = goals.filter(g => g.goal_category === 'priority');
  const additionalGoals = goals.filter(g => g.goal_category === 'additional');

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading goals...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-4xl text-red-600 mb-2"></i>
        <h3 className="text-lg font-bold text-red-900 dark:text-red-300 mb-2">Error Loading Goals</h3>
        <p className="text-red-700 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // No goals state
  if (goals.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 text-center">
        <i className="ri-flag-line text-4xl text-blue-600 mb-2"></i>
        <h3 className="text-lg font-bold text-blue-900 dark:text-blue-300 mb-2">No Goals Set</h3>
        <p className="text-blue-700 dark:text-blue-400 mb-4">
          Goals will be initialized after completing your onboarding.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Experimental Banner */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl p-4 border-2 border-orange-300 dark:border-orange-700">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-white text-sm"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-orange-900 dark:text-orange-300 mb-1">⚠️ Experimental Goals</h3>
            <p className="text-sm text-orange-800 dark:text-orange-400">Benchmarks for 3 months. Realistic targets will be  set after evaluation.</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Goal Setting (3-Month Timeline)</h2>
          <div className="flex items-center space-x-2">
            {canRefresh && (
              <button
                onClick={handleRefreshGoals}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <i className="ri-refresh-line mr-1"></i>
                Refresh Goals
              </button>
            )}
            <button
              onClick={() => onTooltipShow('Goal Setting - Manual Refresh', 'These goals are set for a 3-month period based on your baseline assessment. However, you can manually refresh them anytime if your business needs change. Manual refresh is especially important if: 1) You work with seasonal keywords (e.g., "summer products" or "holiday sales") that change throughout the year, 2) Your business launches new products or services, 3) Market conditions or competition shifts significantly, or 4) You want to adjust targets based on faster-than-expected progress. Refreshing goals ensures your SEO strategy stays aligned with your current business priorities.')}
              className="text-gray-400 hover:text-blue-500"
            >
              <i className="ri-information-line text-xl"></i>
            </button>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">These goals are set for 3 months based on your baseline. You can manually refresh them anytime if your business needs change.</p>
      </div>

      {/* Priority Goals Section */}
      {priorityGoals.length > 0 && (
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Top 3 Priority Goals</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Most impactful goals for every business</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {priorityGoals.map((goal) => {
              const uiConfig = goalTypeToUI[goal.goal_type] || {};
              // BRD: target_type 'paused' is a genuinely paused goal.
              // 'not_applicable' means Captured Visibility — active tracking, no fixed target.
              const isPaused = goal.goal_type !== 'serp-features' && goal.target_type === 'paused';
              const isNotApplicable = goal.target_type === 'not_applicable' || goal.goal_type === 'serp-features';

              return (
                <div
                  key={goal.id}
                  onClick={() => setSelectedGoal(goal.goal_type)}
                  className={`${isPaused
                    ? 'bg-gray-50 dark:bg-gray-900/20'
                    : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
                    } rounded-lg p-4 shadow-md border-2 transition-all duration-200 cursor-pointer ${selectedGoal === goal.goal_type
                      ? isPaused
                        ? 'border-gray-400 shadow-lg scale-105'
                        : 'border-green-500 shadow-lg scale-105'
                      : isPaused
                        ? 'border-gray-300 dark:border-gray-700 hover:border-gray-400 hover:shadow-lg'
                        : 'border-green-200 dark:border-green-800 hover:border-green-400 hover:shadow-lg'
                    }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${uiConfig.color || 'from-gray-400 to-gray-500'} flex items-center justify-center`}>
                      <i className={`${uiConfig.icon || 'ri-flag-line'} text-xl text-white`}></i>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTooltipShow(uiConfig.title, uiConfig.tooltip);
                      }}
                      className={isPaused ? 'text-gray-500 hover:text-gray-700' : 'text-green-600 hover:text-green-700'}
                    >
                      <i className="ri-information-line text-lg"></i>
                    </button>
                  </div>

                  <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1 flex items-center">
                    {uiConfig.title}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{uiConfig.description}</p>

                  {goal.target_type === 'slabs' && goal.slab_data ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Target Distribution:</p>
                      {goal.slab_data.map((slab, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-700 dark:text-gray-300">{slab.label}</span>
                            <span className="font-bold text-gray-900 dark:text-white">{slab.percentage}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`${slab.color} h-2 rounded-full transition-all duration-300`}
                              style={{ width: `${slab.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{goal.current_value}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{goal.unit}</p>
                        {/* SERP Features Breakdown */}
                        {goal.goal_type === 'serp-features' && goal.serp_features_breakdown && goal.serp_features_breakdown.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs font-semibold text-green-600 dark:text-green-400 mb-1">Active Features:</p>
                            {goal.serp_features_breakdown.map((feature, idx) => (
                              <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">
                                <span className="font-medium text-gray-800 dark:text-gray-200">{feature.type}</span>
                                <div className="text-xs text-gray-400 truncate" title={feature.keyword}>
                                  "{feature.keyword.length > 18 ? feature.keyword.substring(0, 18) + '...' : feature.keyword}"
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* BRD: Captured Visibility has no fixed target — show 'Not Applicable' */}
                      {isNotApplicable ? (
                        <div className="rounded-lg p-2 bg-green-50 dark:bg-green-900/20 border border-dashed border-green-300 dark:border-green-700">
                          <p className="text-xs text-green-600 dark:text-green-400 mb-1">Target</p>
                          <div className="flex items-center space-x-1">
                            <i className="ri-eye-line text-green-500 text-sm"></i>
                            <p className="text-sm font-bold text-green-600 dark:text-green-400">N/A</p>
                          </div>
                          <p className="text-xs text-green-500 dark:text-green-500 mt-0.5">Monitoring</p>
                        </div>
                      ) : (
                        <div className={`rounded-lg p-2 ${isPaused
                          ? 'bg-gray-200 dark:bg-gray-700'
                          : 'bg-green-100 dark:bg-green-900/30'
                          }`}>
                          <p className={`text-xs mb-1 ${isPaused
                            ? 'text-gray-600 dark:text-gray-400'
                            : 'text-green-700 dark:text-green-400'
                            }`}>
                            Target
                          </p>
                          <div className="flex items-center space-x-1">
                            {goal.target_type === 'growth' && (
                              <i className="ri-arrow-up-line text-green-600 text-sm"></i>
                            )}
                            {isPaused && (
                              <i className="ri-pause-circle-line text-gray-500 text-sm"></i>
                            )}
                            <p className={`text-lg font-bold ${isPaused
                              ? 'text-gray-600'
                              : 'text-green-600'
                              }`}>
                              {goal.target_value}
                            </p>
                          </div>
                          <p className={`text-xs ${isPaused
                            ? 'text-gray-600 dark:text-gray-400'
                            : 'text-green-700 dark:text-green-400'
                            }`}>
                            {goal.unit}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Additional Goals Section */}
      {additionalGoals.length > 0 && (
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Additional Goals</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Supporting objectives to strengthen your SEO</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {additionalGoals.map((goal) => {
              const uiConfig = goalTypeToUI[goal.goal_type] || {};

              return (
                <div
                  key={goal.id}
                  onClick={() => setSelectedGoal(goal.goal_type)}
                  className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border-2 transition-all duration-200 cursor-pointer ${selectedGoal === goal.goal_type
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${uiConfig.color || 'from-gray-400 to-gray-500'} flex items-center justify-center`}>
                      <i className={`${uiConfig.icon || 'ri-flag-line'} text-xl text-white`}></i>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTooltipShow(uiConfig.title, uiConfig.tooltip);
                      }}
                      className="text-gray-400 hover:text-blue-500"
                    >
                      <i className="ri-information-line text-lg"></i>
                    </button>
                  </div>

                  <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1 flex items-center">
                    {uiConfig.title}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{uiConfig.description}</p>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{goal.current_value}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{goal.unit}</p>
                    </div>
                    <div className={`rounded-lg p-2 ${goal.target_type === 'range'
                      ? 'bg-orange-50 dark:bg-orange-900/30'
                      : 'bg-blue-50 dark:bg-blue-900/30'
                      }`}>
                      <p className={`text-xs mb-1 ${goal.target_type === 'range'
                        ? 'text-orange-700 dark:text-orange-400'
                        : 'text-blue-700 dark:text-blue-400'
                        }`}>
                        Target
                      </p>
                      <div className="flex items-center space-x-1">
                        {goal.target_type === 'growth' && (
                          <i className="ri-arrow-up-line text-blue-600 text-sm"></i>
                        )}
                        {goal.target_type === 'range' && (
                          <i className="ri-arrow-down-line text-orange-600 text-sm"></i>
                        )}
                        <p className={`text-lg font-bold ${goal.target_type === 'range'
                          ? 'text-orange-600'
                          : 'text-blue-600'
                          }`}>
                          {goal.target_value}
                        </p>
                      </div>
                      <p className={`text-xs ${goal.target_type === 'range'
                        ? 'text-orange-700 dark:text-orange-400'
                        : 'text-blue-700 dark:text-blue-400'
                        }`}>
                        {goal.unit}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next Step CTA */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between text-white">
          <div>
            <h3 className="text-xl font-bold mb-2">Ready to Take Action?</h3>
            <p className="text-green-100">Let's create a detailed action plan to achieve these goals</p>
          </div>
          <button
            onClick={handleCreateActionPlan}
            className="px-6 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors whitespace-nowrap"
          >
            Create Action Plan →
          </button>
        </div>
      </div>
    </div>
  );
}
